import { Images } from '@/assets';
import CommonTitle from '@/components/title/CommonTitle';
import { colors } from '@/theme/colors';
import styled from 'styled-components';
import ProfileImgUpload from './profile-settings/ProfileImgUpload';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLoading } from '@/context/LoadingContext';
import { getPartnerBusinessProfile } from '@/api/business-profile/getPartnerBusinessProfile';
import { useDispatch } from 'react-redux';
import { businessProfile } from '@/stores/slices/business-profile/businessProfileSlice';
import { useAppSelector } from '@/stores/hooks';
import { putPartnerBusinessProfile } from '@/api/business-profile/putPartnerBusinessProfile';
import { useTranslation } from 'react-i18next';
import { auth } from '@/stores/slices/authSlices';
import AccountInfo from './account-info/AccountInfo';
import CenterInfo from './profile-settings/CenterInfo';
import CenterDetailInfo from './profile-info/bottom/CenterDetailInfo';

const BusinessProfilePage = () => {
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const { startLoading, stopLoading } = useLoading();
  const dispatch = useDispatch();
  const { businessProfile: profile, putBusinessProfile } = useAppSelector(
    (state) => state.businessProfile
  );
  const { t, i18n } = useTranslation();

  const fetchBusinessProifle = useCallback(async () => {
    startLoading();
    try {
      const res = await getPartnerBusinessProfile();
      if (res.isSuccess) {
        dispatch(businessProfile.setBusinessProfile(res.data));
      }
    } finally {
      stopLoading();
    }
  }, [dispatch, startLoading, stopLoading]);

  useEffect(() => {
    fetchBusinessProifle();
  }, [fetchBusinessProifle]);

  const onClickEdit = useCallback(() => {
    setIsEdit(true);
    dispatch(businessProfile.putBusinessProfile(profile));
  }, [dispatch, profile]);

  const onClickCancel = useCallback(() => {
    setIsEdit(false);
    dispatch(businessProfile.putBusinessProfile(null));
  }, [dispatch]);

  const onClickSvaeButton = useCallback(async () => {
    startLoading();
    if (!putBusinessProfile) return;

    try {
      const res = await putPartnerBusinessProfile(putBusinessProfile);
      if (res.isSuccess) {
        dispatch(businessProfile.setBusinessProfile(putBusinessProfile));
        dispatch(businessProfile.putBusinessProfile(null));
        dispatch(businessProfile.setLangModalOpen(false));
        dispatch(
          auth.setProfileMe({
            profileImageId: putBusinessProfile.profileImage.id,
            partnerName: putBusinessProfile.partnerName,
          })
        );
      }
    } finally {
      stopLoading();
    }
    setIsEdit(false);
  }, [dispatch, putBusinessProfile, startLoading, stopLoading]);

  const saveBtnDisabled = useMemo(() => {
    if (!putBusinessProfile) return true;
    const {
      countryCode,
      partnerName,
      partnerContactNumber,
      partnerHeadName,
      partnerAddress,
      languages,
      businessDays,
    } = putBusinessProfile;
    const isKorean = countryCode === 'KR';

    // 주소 필드 언어별 분기
    const addressFields = isKorean
      ? [
          partnerAddress.postalCode,
          partnerAddress.address1,
          partnerAddress.address2,
        ]
      : [
          partnerAddress.postalCode,
          partnerAddress.address1,
          partnerAddress.address2,
          partnerAddress.city,
          partnerAddress.state,
        ];

    // 중간 필드 체크
    const middleFields = [
      partnerName,
      partnerContactNumber,
      partnerHeadName,
      ...addressFields,
    ];

    const middleNone = middleFields.some((field) => !field);

    // 하단 필드 체크
    const bottomNone =
      !languages.length || !businessDays.some((day) => day.active);

    return middleNone || bottomNone;
  }, [putBusinessProfile]);

  if (!profile) return;
  return (
    <Container>
      <CommonTitle
        imgUrl={Images.NavBusinessProfileIcon}
        title={t('businessProfile')}
      />
      <ProfileContainer>
        <AccountInfo profile={profile} />
        <ProfileSettingSection>
          <ProfileImgUpload isEdit={isEdit} profile={profile} />
          <CenterInfo profile={profile} isEdit={isEdit} />
          <CenterDetailInfo profile={profile} isEdit={isEdit} />
        </ProfileSettingSection>
      </ProfileContainer>
      {!isEdit && (
        <ButtonSection>
          <EditButton onClick={onClickEdit}>{t('modify')}</EditButton>
        </ButtonSection>
      )}
      {isEdit && (
        <ButtonSection>
          <EditButton onClick={onClickSvaeButton} disabled={saveBtnDisabled}>
            {t('save')}
          </EditButton>
          <CancelButton onClick={onClickCancel}>{t('cancel')}</CancelButton>
        </ButtonSection>
      )}
    </Container>
  );
};

export default BusinessProfilePage;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  background: ${colors.white};
  width: 100%;
  flex: 1;
  padding: 50.5px 32px 0 13px;
  box-sizing: border-box;
`;

const ProfileContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 39px;
  margin: 40.5px 0 89px;
`;

const ProfileSettingSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const ButtonSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
`;

const EditButton = styled.button`
  background: #6a96f8;
  border-radius: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 50px;
  width: 184px;
  color: #fffcfc;
  font-size: 20px;
  line-height: 140%;
  &:disabled {
    cursor: auto;
    background: rgba(106, 150, 248, 0.5);
  }
`;

const CancelButton = styled(EditButton)`
  background: #d9d9d9;
  color: #000000;
`;
